import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";

export const DeleteDocumentDialog = ({ workspaceId, doc, onClose, onDeleted }) => {
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setImpact(null);
    if (doc) api.get(`/workspaces/${workspaceId}/documents/${doc.id}/impact`).then(({ data }) => setImpact(data)).catch(() => setImpact(false));
  }, [doc, workspaceId]);

  const remove = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.delete(`/workspaces/${workspaceId}/documents/${doc.id}`);
      toast.success(`Document deleted${data.cancelled_reminders ? `. ${data.cancelled_reminders} pending reminder(s) cancelled.` : "."}`);
      onDeleted();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const n = impact?.pending_reminders ?? 0;
  return (
    <AlertDialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="document-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this document?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-slate-600">
              <p>"{doc?.original_filename}" will be removed from this workspace and can no longer be opened.</p>
              {impact === null && <p>Checking linked reminders...</p>}
              {impact && (
                <p data-testid="document-delete-impact" className={`rounded-md border p-3 ${n ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50"}`}>
                  {n ? `Deleting this document will cancel ${n} pending reminder${n > 1 ? "s" : ""}.` : "No pending reminders are linked to this document."}
                  {impact.linked_deadlines + impact.linked_actions > 0 && ` ${impact.linked_deadlines} deadline(s) and ${impact.linked_actions} action(s) stay, marked as linked to a deleted document.`}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="document-delete-cancel-button">Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={remove} disabled={busy || impact === null} data-testid="document-delete-confirm-button" className="bg-red-600 hover:bg-red-700">
            {busy ? "Deleting..." : "Delete document"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
